import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia, 
  Grid, 
  Button, 
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Create a custom axios instance with the correct base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || 'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOi8vbG9jYWxob3N0IiwiaWF0IjoxNzQyMTE2NjQ2LCJleHAiOjE3NDIxMjAyNDYsIm5iZiI6MTc0MjExNjY0NiwianRpIjoiNk9YZWNxSGNlYTdoUTc4OSIsInN1YiI6IjEiLCJwcnYiOiIyM2JkNWM4OTQ5ZjYwMGFkYjM5ZTcwMWM0MDA4NzJkYjdhNTk3NmY3In0.y3kGqgZq41qAPBhDxucZgn0pngEt6_D79gXBRsaj6PM'}`
  }
});

const BookingVehicleSelection = ({ tourId, bookingDates, onVehicleSelect, selectedVehicleId }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (tourId && bookingDates?.startDate && bookingDates?.endDate) {
      fetchAvailableVehicles();
    }
  }, [tourId, bookingDates]);

  const fetchAvailableVehicles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/api/v1/tours/${tourId}/available-vehicles`, {
        params: {
          start_date: bookingDates.startDate,
          end_date: bookingDates.endDate
        }
      });
      
      if (response.data.success) {
        setVehicles(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch available vehicles');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred while fetching available vehicles');
      console.error('Error fetching available vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    onVehicleSelect(vehicleId);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (vehicles.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No vehicles are available for the selected dates. Please contact us for alternative options.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Available Vehicles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a vehicle for your tour. All vehicles include a professional driver.
      </Typography>
      
      <Grid container spacing={2}>
        {vehicles.map((vehicle) => (
          <Grid item xs={12} md={6} key={vehicle.id}>
            <Card 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                height: '100%',
                border: selectedVehicleId === vehicle.id ? '2px solid #4caf50' : '1px solid #e0e0e0',
                boxShadow: selectedVehicleId === vehicle.id ? '0 0 10px rgba(76, 175, 80, 0.3)' : 'none',
                position: 'relative'
              }}
            >
              {selectedVehicleId === vehicle.id && (
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Selected" 
                  color="success"
                  sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10, 
                    zIndex: 1 
                  }} 
                />
              )}
              
              <CardMedia
                component="img"
                sx={{ 
                  width: { xs: '100%', sm: 140 },
                  height: { xs: 140, sm: '100%' },
                  objectFit: 'cover'
                }}
                image={vehicle.image || 'https://via.placeholder.com/140x140?text=Vehicle'}
                alt={vehicle.name}
              />
              
              <CardContent sx={{ flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
                <Typography component="div" variant="h6">
                  {vehicle.name}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" component="div">
                  {vehicle.make} {vehicle.model} ({vehicle.model_year})
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 1 }}>
                  <DirectionsCarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {vehicle.type}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AirlineSeatReclineNormalIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {vehicle.capacity} passengers
                  </Typography>
                </Box>
                
                {vehicle.features && vehicle.features.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Features: {Array.isArray(vehicle.features) ? vehicle.features.join(', ') : vehicle.features}
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ mt: 'auto', pt: 1 }}>
                  <Button 
                    variant={selectedVehicleId === vehicle.id ? "contained" : "outlined"} 
                    color="primary"
                    onClick={() => handleSelectVehicle(vehicle.id)}
                    fullWidth
                  >
                    {selectedVehicleId === vehicle.id ? "Selected" : "Select Vehicle"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default BookingVehicleSelection;
